import React, { useCallback, useEffect, useMemo } from 'react';

import { Box } from '@mui/material';
import { FormikProps } from 'formik';

import {
  reset,
  getRecurrence,
  ContractTypes,
  getLongTermContractAttributes,
  resetLongTermContractAttributes,
} from '@pharmaplan/common';

import {
  ContractTypesDropdown,
  DurationTypes,
  InvertedRepeatMap,
  RecurrenceTypes,
  ReplacementType,
  serverDateKey,
} from '../../../../helpers/Constants';

import { resetDialog, setDialog } from '../../../../reducers/dialogReducer';
import { ReactComponent as Calendar } from '../../../../assets/svg/calendar.svg';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import {
  getZeroHourDate,
  toStandardFormat,
} from '../../../../helpers/Functions';
import CustomRecurrence from '../../../Modals/CustomRecurrence';
import CustomDatePicker from '../../../common/CustomDatePicker';
import { getDayMonthDateYear } from '../../../Reports/helpers';
import genericClasses from '../../../../theme/GenericClasses';
import CustomTextField from '../../../common/CustomTextField';
import CustomDropdown from '../../../common/CustomDropdown';
import DateTimeRange from '../../../common/DateTimeRange';
import CustomButton from '../../../common/CustomButton';
import strings from '../../../../localization';

import ViewEditContract from './ViewEditContract';
import styles from './styles';

interface IWorkshiftForm {
  formik: FormikProps<any>;
  isSeries: boolean;
  showRecurrences?: boolean;
  disableAll?: boolean;
  customDateName?: string;
  isUpdate?: boolean;
  openedDate?: string;
  hideDatePicker?: boolean;
}

const WorkshiftForm = ({
  formik,
  isSeries,
  showRecurrences = true,
  disableAll,
  customDateName,
  isUpdate,
  openedDate,
  hideDatePicker,
}: IWorkshiftForm) => {
  const dispatch = useAppDispatch();
  const dateError = formik.touched[customDateName ?? 'selectedDate']
    && formik.errors[customDateName ?? 'selectedDate'];

  const recurrence = useAppSelector(getRecurrence);
  const longTermAttributes = useAppSelector(getLongTermContractAttributes);
  const { startDate, endDate } = longTermAttributes ?? {};

  const dateString = useMemo(
    () =>
      startDate
      && endDate
      && `${getDayMonthDateYear(toStandardFormat(startDate))} ${
        strings.to
      } ${getDayMonthDateYear(toStandardFormat(endDate))}`,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [longTermAttributes],
  );

  const isLongTerm = formik.values.contractType === ContractTypes.longTerm;
  // ITEM DISABLED ONLY WHEN UPDATING IN SELF SERVICE
  const isSelfServiceUpdate = isUpdate;
  const disabledSelfService = isSelfServiceUpdate || disableAll;

  // ITEMS DISABLED ONLY WHEN IT IS SINGLE RECURRENCE UPDATE
  const disabledRecurrence = !isSeries || !!disableAll || isLongTerm;

  const enableSetRecurrenceDialog = useCallback(
    (currentRec: DurationTypes) => {
      dispatch(
        setDialog({
          Component: (
            <CustomRecurrence
              date={formik.values.selectedDate}
              currentRecurrence={currentRec}
              primaryBtnFunction={() => {
                dispatch(resetDialog());
              }}
            />
          ),
          showCloseButton: true,
          heading: {
            title: strings.customRecurrence,
          },
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formik.values.selectedDate],
  );

  const handleNegativeDates = () => {
    formik.setFieldValue('endTime', null);
  };

  const serverToday = getZeroHourDate(
    localStorage.getItem(serverDateKey) ?? '',
  );

  const showViewEditContract = async () => {
    dispatch(
      setDialog({
        Component: (
          <ViewEditContract openedDate={openedDate} isUpdate={isUpdate} />
        ),
        maxWidth: 'lg',
        heading: {
          title: strings.chooseDates,
        },
        showCloseButton: true,
      }),
    );
  };

  useEffect(() => {
    formik.setFieldValue(
      'recurrence',
      InvertedRepeatMap[recurrence.repeatEvery],
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recurrence.repeatEvery]);

  useEffect(
    () =>
      () => {
        dispatch(reset());
      // eslint-disable-next-line react-hooks/exhaustive-deps
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    formik.setFieldValue('displayDate', dateString);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateString]);

  useEffect(
    () =>
      () => {
        dispatch(resetLongTermContractAttributes());
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <Box component="div">
      <CustomDropdown
        formik={formik}
        label={strings.typeReplacement}
        name="replacementType"
        disabled={isUpdate}
        menuItems={ReplacementType()}
        customClass={genericClasses.borderNoneTextField}
      />

      <CustomDropdown
        formik={formik}
        label={strings.contractType}
        name="contractType"
        disabled={isUpdate}
        menuItems={ContractTypesDropdown()}
        customClass={genericClasses.borderNoneTextField}
      />

      {isLongTerm ? (
        <Box sx={styles.dateContainer}>
          <CustomTextField
            label={strings.chooseDates}
            disabled
            name="displayDate"
            formik={formik}
            customClass={styles.field}
            EndAdornment={<Calendar />}
            id="name"
          />
          <CustomButton
            customButtonStyle={styles.selectButton}
            onClick={showViewEditContract}
            customClass={styles.select}
            label={strings.select}
          />
        </Box>
      ) : (
        !hideDatePicker && (
          <CustomDatePicker
            formik={formik}
            error={dateError as string}
            minDate={serverToday}
            disabled={disabledSelfService}
            label={strings.selectedDate}
            name={customDateName ?? 'selectedDate'}
            readOnly
          />
        )
      )}

      {!isLongTerm && (
        <Box component="div" sx={styles.timeContainer}>
          <DateTimeRange
            formik={formik}
            startName="startTime"
            endName="endTime"
            customStartHandler={handleNegativeDates}
            allowValidOnly={false}
            startPlaceholder="00:00"
            endPlaceholder="00:00"
            startNameLabel={strings.startTime}
            endNameLabel={strings.endTime}
            horizontal={false}
            removeMargin
            disabled={disableAll}
          />
        </Box>
      )}

      {showRecurrences && !isLongTerm && (
        <Box component="div" sx={styles.recurrenceContainer}>
          <CustomDropdown
            name="recurrence"
            isRecurrence
            disabled={disabledRecurrence}
            label={strings.recurrences}
            formik={formik}
            onClickMenuItem={(val) => {
              if (val !== 'notRepeat') {
                enableSetRecurrenceDialog(val as DurationTypes);
              }
            }}
            menuItems={RecurrenceTypes()}
          />
        </Box>
      )}
    </Box>
  );
};
export default WorkshiftForm;
